import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Grade 7 number.
 *
 * The year arithmetic *closes*. Until now a student has met three separate
 * systems — whole numbers, fractions, decimals — and negatives arrived as a
 * fourth. This lesson's job is to say that there was only ever one system, and
 * that the rules of sign are the same throughout it.
 */
export const RATIONAL_NUMBER_LESSONS: Lesson[] = [
  lesson({
    id: 'rational-numbers-01',
    unitId: 'unit-rational-numbers',
    conceptId: 'rational-numbers',
    title: 'Operations with Rational Numbers',
    summary: 'Fractions, decimals and negatives are one system, not three — and the sign rules are the same for all of them.',
    objectives: [
      'Add and subtract signed numbers, including subtracting a negative',
      'Decide the sign of a product or quotient by counting negative factors',
      'Move between fraction and decimal forms of the same number',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'One number line, not three',
        blocks: [
          text('You have met whole numbers, then fractions, then decimals, then negatives — and each arrived as though it were a new subject with new rules to memorise.'),
          text('It is time to say plainly that they are all the same thing. A **rational number** is any number that can be written as one integer over another.'),
          math('7 = \\frac{7}{1} \\qquad 0.25 = \\frac{1}{4} \\qquad -3\\tfrac{1}{2} = \\frac{-7}{2}'),
          text('Every number on that list is rational, and every rule below applies to all of them equally. There is nothing left to add to arithmetic this year — only one set of rules to finish learning.'),
        ],
      },
      intuition: {
        title: 'Direction, then distance',
        blocks: [
          text('Adding is movement along the number line. The sign of the number you are adding says which way to go; its size says how far.'),
          figure(
            { kind: 'number-line', from: -6, to: 6, step: 1, marks: [{ at: -4, label: 'start' }, { at: 2, label: 'end' }] },
            'Starting at −4 and adding 6 moves six to the right, landing on 2.',
          ),
          insight('Subtracting is adding the opposite. That single sentence removes subtraction from the list of things you have to think about: 5 − 8 is 5 + (−8), and 5 − (−3) is 5 + 3.'),
          text('So *subtracting a negative adds*. This is not a trick, and it is not arbitrary. If a friend cancels a £3 debt you owe, taking away that −3 leaves you £3 better off.'),
          warning('The common error is to read 5 − (−3) as 5 − 3 = 2, quietly losing one of the two minus signs. Write the rewrite down — 5 + 3 — rather than doing it in your head.', 'Where this goes wrong'),
          text('Multiplication has its own rule, and it is worth knowing *why* rather than only *what*.'),
          math('3 \\times (-4) = -12'),
          text('Three lots of −4 is −12; that much is just repeated addition. But then what is (−3) × (−4)? Multiplying by −3 is multiplying by 3 and then taking the opposite. The opposite of −12 is +12.'),
          insight('So: count the negative signs. An even number of them cancel in pairs and the answer is positive; an odd number leaves one over and the answer is negative. This holds for division too, because dividing is multiplying by the reciprocal.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['Calculation', 'Rewritten', 'Result'], [
            ['−7 + 3', 'move 3 right from −7', '−4'],
            ['−7 − 3', '−7 + (−3)', '−10'],
            ['−7 − (−3)', '−7 + 3', '−4'],
            ['(−7) × (−3)', 'two negatives', '21'],
            ['(−21) ÷ 3', 'one negative', '−7'],
          ]),
          text('Notice that the second and third rows differ by one sign and by six units. That gap is exactly what gets lost when a double negative is not written out.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Work out −8 − (−5) × 2.'),
          steps([
            { label: 'Order of operations still applies. Multiplication first.', latex: '(-5) \\times 2 = -10' },
            { label: 'One negative sign, so the product is negative.', latex: '-8 - (-10)' },
            { label: 'Subtracting a negative is adding.', latex: '-8 + 10' },
            { label: 'Move 10 to the right from −8.', latex: '= 2' },
          ]),
          tip('Rewrite every subtraction as an addition *before* you start calculating. Almost every sign error in this topic happens in the step that was done mentally.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('rational-numbers', 4)] },
      independent: { title: 'On your own', blocks: [practice('rational-numbers', 6)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('rational-numbers', 3, 'Signed fractions and decimals, where the sign rules and the arithmetic have to work together.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A rational number is one integer over another — whole numbers, fractions and terminating decimals are all included.',
            'Subtracting is adding the opposite, so subtracting a negative adds.',
            'For multiplication and division, count the negative signs: an even number gives a positive answer, an odd number a negative one.',
            'The rules do not change when the numbers are fractions or decimals.',
          ]),
        ],
      },
    },
  }),
];
